// Wordlist Health Scan
// Comprehensive validation of solution word list

import { ScanResult, createScanSummary, ScanSummary } from './types';
import { solutionWords, obfuscationInfo } from '@/data/processedSolutions';
import { getShuffledSolutions } from '@/utils/wordlistEngine';
import { runFullWordlistValidation, checkVowelDistribution } from '@/utils/wordlistValidator';

export function runWordlistScan(autoFix: boolean = false): ScanSummary {
  console.group('📚 WORDLIST HEALTH SCAN');
  console.log('Analyzing solution word list quality and safety...\n');
  
  const results: ScanResult[] = [];
  const startTime = Date.now();
  
  // Get both original and shuffled lists
  const originalWords = solutionWords;
  const shuffledWords = getShuffledSolutions();
  
  // Run comprehensive validation
  const validation = runFullWordlistValidation(originalWords);
  
  // Check 1: Total word count
  if (originalWords.length >= 2000 && originalWords.length <= 3500) {
    results.push({
      name: 'Word Count',
      status: 'pass',
      message: `Word list contains ${originalWords.length} solutions (target: 2000-3500)`
    });
  } else if (originalWords.length < 2000) {
    results.push({
      name: 'Word Count',
      status: 'warn',
      message: `Word list is small: ${originalWords.length} words (target: 2000-3500)`
    });
  } else {
    results.push({
      name: 'Word Count',
      status: 'info',
      message: `Word list contains ${originalWords.length} solutions`
    });
  }
  
  // Check 2: Duplicates
  if (validation.duplicates.length === 0) {
    results.push({
      name: 'Duplicates',
      status: 'pass',
      message: 'No duplicate words found'
    });
  } else {
    results.push({
      name: 'Duplicates',
      status: 'fail',
      message: `Found ${validation.duplicates.length} duplicate words`,
      details: `Duplicates: ${validation.duplicates.slice(0, 10).join(', ')}${validation.duplicates.length > 10 ? '...' : ''}`
    });
  }
  
  // Check 3: Inappropriate words
  if (validation.inappropriateWords.length === 0) {
    results.push({
      name: 'Inappropriate Words',
      status: 'pass',
      message: 'No inappropriate or offensive words found'
    });
  } else {
    const wasAutoFixed = autoFix && validation.inappropriateWords.length > 0;
    results.push({
      name: 'Inappropriate Words',
      status: wasAutoFixed ? 'warn' : 'fail',
      message: `Found ${validation.inappropriateWords.length} inappropriate/offensive words`,
      details: wasAutoFixed 
        ? `Words detected (auto-filter enabled): ${validation.inappropriateWords.join(', ')}`
        : `Words: ${validation.inappropriateWords.join(', ')}`,
      autoFixed: wasAutoFixed
    });
    
    if (autoFix) {
      console.warn('🔧 Auto-fix: These words should be added to blocklist.ts:');
      validation.inappropriateWords.forEach(word => {
        console.warn(`   - "${word}"`);
      });
      console.warn('🔧 After adding to blocklist, words will be filtered from puzzle rotation');
    }
  }
  
  // Check 4: Invalid format
  if (validation.invalidFormat.length === 0) {
    results.push({
      name: 'Word Format',
      status: 'pass',
      message: 'All words are valid 5-letter A-Z format'
    });
  } else {
    results.push({
      name: 'Word Format',
      status: 'fail',
      message: `Found ${validation.invalidFormat.length} words with invalid format`,
      details: `Invalid: ${validation.invalidFormat.slice(0, 10).join(', ')}`
    });
  }
  
  // Check 5: Alphabetical patterns in ORIGINAL list
  if (validation.alphabeticalPatterns.length === 0) {
    results.push({
      name: 'Original List Order',
      status: 'pass',
      message: 'No alphabetical patterns detected in original list'
    });
  } else {
    results.push({
      name: 'Original List Order',
      status: 'info',
      message: `Found ${validation.alphabeticalPatterns.length} alphabetical patterns in original list`,
      details: 'Original list is alphabetically organized for maintenance (expected - shuffle produces random order)'
    });
  }
  
  // Check 6: Proper nouns
  if (validation.properNouns.length === 0) {
    results.push({
      name: 'Proper Nouns',
      status: 'pass',
      message: 'No common proper nouns found'
    });
  } else {
    results.push({
      name: 'Proper Nouns',
      status: 'warn',
      message: `Found ${validation.properNouns.length} potential proper nouns`,
      details: `Words: ${validation.properNouns.join(', ')}`
    });
  }
  
  // Check 7: Trademark words
  if (validation.trademarkWords.length === 0) {
    results.push({
      name: 'Trademark Words',
      status: 'pass',
      message: 'No trademark/brand words found'
    });
  } else {
    results.push({
      name: 'Trademark Words',
      status: 'warn',
      message: `Found ${validation.trademarkWords.length} trademark/brand words`,
      details: `Words: ${validation.trademarkWords.join(', ')}`
    });
  }
  
  // Check 8: Vowel distribution (difficulty balance)
  const vowelDist = checkVowelDistribution(originalWords);
  const veryHardPercent = (vowelDist.veryHard / originalWords.length) * 100;
  const veryEasyPercent = (vowelDist.veryEasy / originalWords.length) * 100;
  
  if (veryHardPercent < 20 && veryEasyPercent < 20) {
    results.push({
      name: 'Difficulty Balance',
      status: 'pass',
      message: `Good vowel distribution (${vowelDist.balanced} balanced, ${vowelDist.veryHard} hard, ${vowelDist.veryEasy} easy)`
    });
  } else {
    results.push({
      name: 'Difficulty Balance',
      status: 'info',
      message: `Vowel distribution: ${veryHardPercent.toFixed(1)}% very hard, ${veryEasyPercent.toFixed(1)}% very easy`,
      details: `${vowelDist.balanced} balanced words, ${vowelDist.veryHard} very hard (0-1 vowels), ${vowelDist.veryEasy} very easy (4-5 vowels)`
    });
  }
  
  // Check 9: Shuffle integrity
  if (shuffledWords.length === originalWords.length) {
    results.push({
      name: 'Shuffle Integrity',
      status: 'pass',
      message: 'Shuffled word list maintains correct length'
    });
  } else {
    results.push({
      name: 'Shuffle Integrity',
      status: 'fail',
      message: `Shuffled list length mismatch: ${shuffledWords.length} vs ${originalWords.length}`
    });
  }
  
  // Check 10: External word list overlap
  if (validation.externalOverlap.length === 0) {
    results.push({
      name: 'External Overlap',
      status: 'pass',
      message: 'No detected overlap with external word lists'
    });
  } else {
    results.push({
      name: 'External Overlap',
      status: 'info',
      message: `Found ${validation.externalOverlap.length} words that may overlap with external lists`,
      details: `Common 5-letter words naturally appear across word lists. Words: ${validation.externalOverlap.join(', ')}`
    });
  }
  
  // Check 11: Obfuscation layer
  if (obfuscationInfo.isObfuscated) {
    results.push({
      name: 'Word List Obfuscation',
      status: 'pass',
      message: 'Word list uses client-side obfuscation',
      details: `Hash: ${obfuscationInfo.hash}, Chunks: ${obfuscationInfo.chunkCount}`
    });
  } else {
    results.push({
      name: 'Word List Obfuscation',
      status: 'warn',
      message: 'Word list is not obfuscated (easily extractable)'
    });
  }
  
  const duration = ((Date.now() - startTime) / 1000).toFixed(2);
  
  console.log('\n' + '='.repeat(50));
  console.log('📊 WORDLIST HEALTH SUMMARY');
  console.log('='.repeat(50));
  console.log(`Total Words: ${originalWords.length}`);
  console.log(`Valid Words: ${validation.validWords}`);
  console.log(`Duplicates: ${validation.duplicates.length}`);
  console.log(`Inappropriate: ${validation.inappropriateWords.length}`);
  console.log(`Invalid Format: ${validation.invalidFormat.length}`);
  console.log(`Duration: ${duration}s`);
  console.log('='.repeat(50));
  console.groupEnd();
  
  return createScanSummary('Wordlist Health Scan', results);
}
